import {
    domainColumns,
    formatOutOf,
    formatYesNo,
    getScale,
    getScales,
    getString,
    mapScreeningLabel,
    type OutcomeTableConfig,
} from '../helpers';

// Shared by scared and scaredp: the api parent version re-exports the
// child/adolescent calculator, so both emit identical outcome shapes.
export const scaredConfig: OutcomeTableConfig = {
    title: 'Resultados SCARED',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');
        const panicSomatic = getScale(scales, 'panic_somatic');
        const generalizedAnxiety = getScale(scales, 'generalized_anxiety');
        const separationAnxiety = getScale(scales, 'separation_anxiety');
        const socialAnxiety = getScale(scales, 'social_anxiety');
        const schoolAvoidance = getScale(scales, 'school_avoidance');

        return [
            {
                domain: 'Sintomas de ansiedade (total SCARED)',
                result: formatOutOf(total.raw, 82),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Pânico/Sintomas somáticos',
                result: formatOutOf(panicSomatic.raw, 26),
                interpretation: getString(panicSomatic.interpretation_label),
            },
            {
                domain: 'Ansiedade generalizada',
                result: formatOutOf(generalizedAnxiety.raw, 18),
                interpretation: getString(generalizedAnxiety.interpretation_label),
            },
            {
                domain: 'Ansiedade de separação',
                result: formatOutOf(separationAnxiety.raw, 16),
                interpretation: getString(separationAnxiety.interpretation_label),
            },
            {
                domain: 'Ansiedade social',
                result: formatOutOf(socialAnxiety.raw, 14),
                interpretation: getString(socialAnxiety.interpretation_label),
            },
            {
                domain: 'Evitação escolar',
                result: formatOutOf(schoolAvoidance.raw, 8),
                interpretation: getString(schoolAvoidance.interpretation_label),
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return (
            'screening_positive' in getScale(scales, 'panic_somatic') &&
            'screening_positive' in getScale(scales, 'school_avoidance')
        );
    },
};

const SCAS_SUBSCALE_INTERPRETATION = 'Subescala dimensional: maior pontuação = mais sintomas';

// Shared by scas and scasp: both api calculators reuse buildScasScales, so
// the outcome keys are identical (only the item mappings differ).
export const scasConfig: OutcomeTableConfig = {
    title: 'Resultados SCAS',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Sintomas de ansiedade (total SCAS)',
                result: formatOutOf(total.raw, 114),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Ansiedade de separação',
                result: formatOutOf(getScale(scales, 'separation_anxiety').raw, 18),
                interpretation: SCAS_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Fobia social',
                result: formatOutOf(getScale(scales, 'social_phobia').raw, 18),
                interpretation: SCAS_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Obsessivo-compulsivo',
                result: formatOutOf(getScale(scales, 'obsessive_compulsive').raw, 18),
                interpretation: SCAS_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Pânico/Agorafobia',
                result: formatOutOf(getScale(scales, 'panic_agoraphobia').raw, 27),
                interpretation: SCAS_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Medos de ferimentos físicos',
                result: formatOutOf(getScale(scales, 'physical_injury_fears').raw, 15),
                interpretation: SCAS_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Ansiedade generalizada',
                result: formatOutOf(getScale(scales, 'generalized_anxiety').raw, 18),
                interpretation: SCAS_SUBSCALE_INTERPRETATION,
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return (
            'raw' in getScale(scales, 'physical_injury_fears') &&
            'raw' in getScale(scales, 'panic_agoraphobia')
        );
    },
};

const CASO_DIMENSION_INTERPRETATION = 'Dimensão (6-30): maior pontuação = mais ansiedade social';

export const casoConfig: OutcomeTableConfig = {
    title: 'Resultados CASO',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Ansiedade social (total CASO)',
                result: formatOutOf(total.raw, 150),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Falar em público/Interação com autoridades',
                result: formatOutOf(getScale(scales, 'public_speaking_authority').raw, 30),
                interpretation: CASO_DIMENSION_INTERPRETATION,
            },
            {
                domain: 'Expressão assertiva de desagrado',
                result: formatOutOf(getScale(scales, 'assertive_expression').raw, 30),
                interpretation: CASO_DIMENSION_INTERPRETATION,
            },
            {
                domain: 'Interação com o sexo oposto',
                result: formatOutOf(getScale(scales, 'opposite_sex').raw, 30),
                interpretation: CASO_DIMENSION_INTERPRETATION,
            },
            {
                domain: 'Interação com desconhecidos',
                result: formatOutOf(getScale(scales, 'strangers').raw, 30),
                interpretation: CASO_DIMENSION_INTERPRETATION,
            },
            {
                domain: 'Ficar em evidência/Passar vergonha',
                result: formatOutOf(getScale(scales, 'embarrassment').raw, 30),
                interpretation: CASO_DIMENSION_INTERPRETATION,
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return (
            'raw' in getScale(scales, 'public_speaking_authority') &&
            'raw' in getScale(scales, 'embarrassment')
        );
    },
};

const OCIR_SUBSCALE_INTERPRETATION = 'Subescala (0-12)';

export const ocirConfig: OutcomeTableConfig = {
    title: 'Resultados OCI-R',
    columns: domainColumns,
    getRows: (outcomes) => {
        const scales = getScales(outcomes);
        const total = getScale(scales, 'total');

        return [
            {
                domain: 'Sintomas obsessivo-compulsivos (total OCI-R)',
                result: formatOutOf(total.raw, 72),
                interpretation: getString(total.interpretation_label),
            },
            {
                domain: 'Rastreio clínico (ponto de corte >= 21)',
                result: formatYesNo(total.screening_positive),
                interpretation: mapScreeningLabel(total.screening_positive),
            },
            {
                domain: 'Verificação',
                result: formatOutOf(getScale(scales, 'checking').raw, 12),
                interpretation: OCIR_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Lavagem',
                result: formatOutOf(getScale(scales, 'washing').raw, 12),
                interpretation: OCIR_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Ordenação',
                result: formatOutOf(getScale(scales, 'ordering').raw, 12),
                interpretation: OCIR_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Acumulação',
                result: formatOutOf(getScale(scales, 'hoarding').raw, 12),
                interpretation: OCIR_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Obsessão',
                result: formatOutOf(getScale(scales, 'obsessing').raw, 12),
                interpretation: OCIR_SUBSCALE_INTERPRETATION,
            },
            {
                domain: 'Neutralização',
                result: formatOutOf(getScale(scales, 'neutralizing').raw, 12),
                interpretation: OCIR_SUBSCALE_INTERPRETATION,
            },
        ];
    },
    match: (outcomes) => {
        const scales = getScales(outcomes);
        return 'raw' in getScale(scales, 'checking') && 'raw' in getScale(scales, 'neutralizing');
    },
};
